import React, { useRef, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { ContactsList } from '../../components/ContactsList';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ContactsSlideOverProps {
  visible: boolean;
  onClose: () => void;
  /** Panel width in px — full viewport width on mobile, docked width on
   * tablet/desktop. Computed by the parent from useWindowDimensions. */
  panelWidth: number;
}

/** Animated slide-over wrapping the existing standalone ContactsList
 * (compact mode) — a backdrop + right-docked panel, both driven by their own
 * Animated.Value so the panel's transform can use the native driver while
 * the backdrop's opacity fades in parallel. Not RN's Modal: this renders as
 * a plain absolutely-positioned overlay inside DashboardScreen's own flex
 * tree, so it composes naturally at any width instead of fighting Modal's
 * own layout/portal behavior on web. */
export const ContactsSlideOver: React.FC<ContactsSlideOverProps> = ({ visible, onClose, panelWidth }) => {
  const styles = useThemedStyles(getStyles);
  const translateX = useRef(new Animated.Value(panelWidth)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  // Keep the panel mounted through its close animation instead of unmounting
  // the instant `visible` flips false, so the slide-out is visible rather
  // than an abrupt disappearance.
  const [mounted, setMounted] = React.useState(visible);

  useEffect(() => {
    if (visible) setMounted(true);
    Animated.parallel([
      Animated.timing(translateX, {
        toValue: visible ? 0 : panelWidth,
        duration: 220,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(backdropOpacity, {
        toValue: visible ? 1 : 0,
        duration: 220,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished && !visible) setMounted(false);
    });
  }, [visible, panelWidth, translateX, backdropOpacity]);

  if (!mounted) return null;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <Animated.View style={[styles.backdrop, { opacity: backdropOpacity }]}>
        <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onClose} />
      </Animated.View>
      <Animated.View style={[styles.panel, { width: panelWidth, transform: [{ translateX }] }]}>
        <View style={styles.panelHeader}>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="close" size={22} color={colors.text.primary as string} />
          </TouchableOpacity>
        </View>
        <ContactsList showHeader showSearch showFilters compact />
      </Animated.View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.background.overlay,
  },
  panel: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: colors.background.surfaceRaised,
    borderLeftWidth: 1,
    borderLeftColor: colors.border,
    ...designTokens.shadows.lg,
  },
  panelHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: designTokens.spacing.sm,
  },
});
